"use client";

import { useState, useContext } from "react";
import { Card, Form, Table, Button } from "react-bootstrap";
import Modal from "react-modal";
import { AuthContext } from "../../../../context/AuthContext";
import { useDashboardData } from "../../../../hooks/useDashboardData";

Modal.setAppElement("#root");

const AppUsageList = () => {
  const { user } = useContext(AuthContext);
  const { recentEntries } = useDashboardData(user.id);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [capturingId, setCapturingId] = useState(null);
  const [localScreenshots, setLocalScreenshots] = useState({});

  const handleCapture = async (userId, entryId, status) => {
    const normalizedStatus = (status || "").toLowerCase();

    if (normalizedStatus === "offline") {
      return alert("Member is offline. Screenshot blocked.");
    }

    if (normalizedStatus === "break") {
      return alert("Member is on break. Screenshot blocked.");
    }

    if (normalizedStatus === "meeting") {
      return alert("Member is in a meeting. Screenshot blocked.");
    }

    setCapturingId(entryId);

    try {
      const res = await fetch(
        "https://time-tracker-gx97.onrender.com/api/screenshots/request",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, time_entry_id: entryId }),
        }
      );

      const data = await res.json();
      if (!res.ok) return alert(data.message || "Capture request blocked");

      alert("Screenshot request sent!");

      if (data.file_url) {
        setLocalScreenshots((prev) => ({
          ...prev,
          [entryId]: data.file_url,
        }));
      }
    } catch (err) {
      alert("Failed to send capture request");
    } finally {
      setCapturingId(null);
    }
  };

  const openModal = (url) => {
    setSelectedImage(url);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
  };

  return (
    <Card className="bg-white border-0 rounded-3 mb-4" style={{ minHeight: '470 px' }}>
      <Card.Body className="p-0">
        <div className="p-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <h3 className="mb-0">Currently Using Apps</h3>
        </div>

        <div className="default-table-area style-two all-projects">
          <div className="table-responsive">
            <Table className="align-middle">
              <thead style={{ textAlign: 'center' }}>
                <tr>
                  <th>ID</th>
                  <th style={{ minWidth: "160px" }}>Employee Name</th>
                  <th>App Name</th>
                  <th>URL</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentEntries.length > 0 ? (
                  recentEntries.map((entry, i) => {
                    const latestScreenshot =
                      localScreenshots[entry.id] || entry.file_url;
                    const isCapturing = capturingId === entry.id;

                    return (
                      <tr key={entry.id} style={{ backgroundColor: 'white' }}>
                        <td>{i + 1}</td>

                        <td>
                          <div className="d-flex align-items-center">
                            <div className="flex-grow-1 ms-2 position-relative top-1">
                              <h6 className="mb-0 fs-14 fw-medium">
                                {entry.name}
                              </h6>
                            </div>
                          </div>
                        </td>

                        <td>{entry.last_active_window || "-"}</td>
                        <td title={entry.last_window_title || "-"}>
                          {entry.last_window_title || "-"}
                        </td>

                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={isCapturing}
                              onClick={() =>
                                handleCapture(entry.user_id, entry.id, entry.status)
                              }
                            >
                              {isCapturing ? "Capturing..." : "Capture"}
                            </Button>

                            {latestScreenshot && (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => openModal(latestScreenshot)}
                              >
                                Preview
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </Card.Body>

      <Modal
        isOpen={modalOpen}
        onRequestClose={closeModal}
        contentLabel="Screenshot Preview"
        style={{
          content: {
            maxWidth: "800px",
            maxHeight: "80vh",
            margin: "auto",
            padding: "1rem",
            borderRadius: "0.5rem",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "#fff",
          },
          overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1050 },
        }}
      >
        <button
          onClick={closeModal}
          style={{
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            border: "none",
            background: "transparent",
            fontSize: "1.5rem",
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          &times;
        </button>

        {selectedImage && (
          <img
            src={selectedImage}
            alt="Screenshot"
            className="img-fluid"
            style={{ maxHeight: "70vh", objectFit: "contain" }}
          />
        )}
      </Modal>
    </Card>
  );
};

export default AppUsageList;